import { Button, JoonUIColor, SPACING, Typography } from "@joonapp/web-shared"
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { loadStripe, type StripeElementLocale } from "@stripe/stripe-js"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { CloseButton } from "../../components/closeButton/CloseButton"
import SlideUpModal from "../../components/slideUpModal/SlideUpModal"
import { QUERY_KEYS } from "../../constants"
import { useUserQuery } from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"

interface StripeCheckoutModalStore {
  isOpen: boolean
  clientSecret: string | null
  stripePublishableKey: string | null
  customerSessionClientSecret: string | null
  hasTrial: boolean
  trialDays: number | null
  onOpen: (
    clientSecret: string,
    stripePublishableKey: string,
    hasTrial: boolean,
    customerSessionClientSecret: string | null,
    trialDays: number | null
  ) => void
  onClose: () => void
}

export const useStripeCheckoutModalStore = create<StripeCheckoutModalStore>(
  (set) => ({
    isOpen: false,
    clientSecret: null,
    stripePublishableKey: null,
    customerSessionClientSecret: null,
    hasTrial: false,
    trialDays: null,
    onOpen: (
      clientSecret,
      stripePublishableKey,
      hasTrial,
      customerSessionClientSecret,
      trialDays
    ) =>
      set({
        isOpen: true,
        clientSecret,
        stripePublishableKey,
        hasTrial,
        customerSessionClientSecret,
        trialDays,
      }),
    onClose: () =>
      set({
        isOpen: false,
        clientSecret: null,
        stripePublishableKey: null,
        customerSessionClientSecret: null,
        hasTrial: false,
        trialDays: null,
      }),
  })
)

let stripePromise: ReturnType<typeof loadStripe> | null = null
let loadedPublishableKey: string | null = null

function getStripePromise(publishableKey: string) {
  if (!stripePromise || loadedPublishableKey !== publishableKey) {
    stripePromise = loadStripe(publishableKey)
    loadedPublishableKey = publishableKey
  }
  return stripePromise
}

function isIOSDevice() {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPad Pro reports as MacIntel with touch points
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  )
}

const TrialBanner = ({ trialDays }: { trialDays: number | null }) => {
  const { t } = useTranslation()
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: SPACING.space2 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: `1px solid ${JoonUIColor.border.default}`,
          borderRadius: 12,
          padding: `${SPACING.space3} ${SPACING.space4}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
            {t("PAYWALL_STRIPE_DUE_TODAY")}
          </Typography>
          <Typography variant="h2" color={JoonUIColor.semantic.success}>
            $0.00
          </Typography>
        </div>
        <div
          style={{
            background: JoonUIColor.background.accent,
            borderRadius: 20,
            padding: `${SPACING.space1} ${SPACING.space3}`,
          }}
        >
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.inverted}
            style={{ fontWeight: 700 }}
          >
            {trialDays
              ? t("PAYWALL_STRIPE_TRIAL_BADGE_DAYS", { days: trialDays })
              : t("PAYWALL_STRIPE_FREE_TRIAL")}
          </Typography>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: SPACING.space2,
          padding: `${SPACING.space3} ${SPACING.space4}`,
          background: JoonUIColor.background.lightGray,
          borderRadius: 12,
        }}
      >
        <Typography variant="bodySmall">
          <span style={{ fontWeight: 700 }}>
            {t("PAYWALL_STRIPE_NO_CHARGE")}
          </span>{" "}
          {t("PAYWALL_STRIPE_REMINDER")}
        </Typography>
      </div>
    </div>
  )
}

const CheckoutForm = ({
  hasTrial,
  trialDays,
  onClose,
}: {
  hasTrial: boolean
  trialDays: number | null
  onClose: () => void
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { user } = useUserQuery()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const returnUrl = `${window.location.origin}/me?paid=true`

  const handlePayment = async ({ skipSubmit = false } = {}) => {
    if (!stripe || !elements) return

    setIsLoading(true)
    setErrorMessage(null)

    try {
      if (!skipSubmit) {
        const { error: submitError } = await elements.submit()
        if (submitError) {
          setErrorMessage(
            submitError.message ?? t("PAYWALL_STRIPE_ERROR_GENERIC")
          )
          return
        }
      }

      const billingDetails = user?.email
        ? { billing_details: { email: user.email } }
        : {}

      const result = hasTrial
        ? await stripe.confirmSetup({
            elements,
            confirmParams: {
              return_url: returnUrl,
              payment_method_data: billingDetails,
            },
            redirect: "if_required",
          })
        : await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: returnUrl,
              payment_method_data: billingDetails,
            },
            redirect: "if_required",
          })

      if (result.error) {
        trackAnalyticEvent(ANALYTIC_EVENTS.CHECKOUT_ERROR, {
          error_code: result.error.code,
          decline_code: (result.error as { decline_code?: string })
            .decline_code,
        })
        setErrorMessage(
          result.error.message ?? t("PAYWALL_STRIPE_ERROR_GENERIC")
        )
      } else {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SUBSCRIBER_INFO],
        })
        onClose()
        router.push("/me?paid=true")
      }
    } catch {
      setErrorMessage(t("PAYWALL_STRIPE_ERROR_GENERIC"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: SPACING.space3 }}
    >
      {hasTrial && <TrialBanner trialDays={trialDays} />}
      {isIOSDevice() && (
        <ExpressCheckoutElement
          options={{
            paymentMethods: { applePay: "auto", googlePay: "never" },
          }}
          onConfirm={() => handlePayment({ skipSubmit: true })}
        />
      )}
      <PaymentElement
        options={{
          layout: { type: "tabs" },
          wallets: { applePay: "never", googlePay: "never" },
          paymentMethodOrder: ["card", "link"],
          defaultValues: {
            billingDetails: { email: user?.email ?? "" },
          },
        }}
      />
      {errorMessage && (
        <Typography
          variant="bodySmall"
          color={JoonUIColor.semantic.destructive}
        >
          {errorMessage}
        </Typography>
      )}
      <Button
        buttonType="primary"
        size="large"
        fullWidth
        isLoading={isLoading || !stripe || !elements}
        onClick={() => handlePayment()}
        text={
          hasTrial ? t("PAYWALL_STRIPE_START_TRIAL") : t("PAYWALL_MOF_UNLOCK")
        }
      />
    </div>
  )
}

const StripeCheckoutModal = () => {
  const {
    isOpen,
    clientSecret,
    stripePublishableKey,
    customerSessionClientSecret,
    hasTrial,
    trialDays,
    onClose,
  } = useStripeCheckoutModalStore()
  const { t, i18n } = useTranslation()

  if (!isOpen || !clientSecret || !stripePublishableKey) return null

  const stripe = getStripePromise(stripePublishableKey)

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
    >
      <div
        style={{
          width: "100%",
          padding: `${SPACING.space6} ${SPACING.space4} ${SPACING.space8}`,
          display: "flex",
          flexDirection: "column",
          gap: SPACING.space4,
          position: "relative",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h3">
          {hasTrial
            ? t("PAYWALL_STRIPE_TITLE_TRIAL")
            : t("PAYWALL_STRIPE_TITLE_PAYMENT")}
        </Typography>
        <Elements
          stripe={stripe}
          options={{
            clientSecret,
            customerSessionClientSecret:
              customerSessionClientSecret ?? undefined,
            // Localize Stripe's built-in field labels/errors to match the app
            // language (not just the browser locale, which is Stripe's default).
            // Our supported languages are all valid Stripe locales, so passing
            // i18n.language through scales automatically as we add more.
            locale: i18n.language as StripeElementLocale,
            appearance: {
              theme: "stripe",
              variables: { fontSizeBase: "16px" },
            },
          }}
        >
          <CheckoutForm
            hasTrial={hasTrial}
            trialDays={trialDays}
            onClose={onClose}
          />
        </Elements>
      </div>
    </SlideUpModal>
  )
}

export default StripeCheckoutModal
